import React, { useState } from "react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/ProjectProgressDashboard.css";
import axios from "axios";
import { Link } from "react-router-dom";
import { Base_URL } from "../url";
import { useEffect } from "react";

const ProjectProgressList = () => {
  const { user } = useAuth();

  const [filters, setFilters] = useState({
    type: "All",
    client: "",
    project: "",
    resource: "",
    fromDate: "",
    toDate: "",
    dueDate: "",
    dueStatus: "",
    status: "All",

    // ✅ NEW FILTERS
    changedFromDate: "",
    changedToDate: "",
    submittedBy: "",
  });

  const [tableData, setTableData] = useState([]);
  const [dropdownData, setDropdownData] = useState({
    clients: [],
    projects: [],
    types: [],
    resources: [],
    submittedBy: [],
    dueStatus: "",
    statuses: [],
  });

  const handleChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApply = async () => {
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => {
          if (value === "" || value === null) return false;
          if (value === "All") return false;
          return true;
        }),
      );

      const res = await axios.get(`${Base_URL}/api/ProjectProgress/list`, {
        params: {
          ...cleanedFilters,
          orgId: user?.organisationId,
        },
      });

      const data = res?.data?.data || res?.data?.Data || res?.data || [];

      // ✅ 🔥 NORMALIZE DATA HERE
      const mappedData = (Array.isArray(data) ? data : []).map((item) => ({
        ...item,

        // FORCE correct values
        orgDisplayName:
          item.orgShortName && item.orgShortName.trim()
            ? item.orgShortName
            : item.orgName,

        clientDisplayName: item.clientShortName || "",
      }));

      // ✅ Extract unique clientShortNames
      const clientShortNames = [
        ...new Set(
          mappedData
            .map((item) => item.clientDisplayName)
            .filter((name) => name && name.trim() !== ""),
        ),
      ];

      setDropdownData((prev) => ({
        ...prev,
        clients: clientShortNames,
      }));

      const filteredData = mappedData.filter((item) => {
        const date = new Date(item.chargedOn);

        if (filters.fromDate && date < new Date(filters.fromDate)) return false;
        if (filters.toDate && date > new Date(filters.toDate)) return false;

        if (filters.changedFromDate && date < new Date(filters.changedFromDate))
          return false;
        if (filters.changedToDate && date > new Date(filters.changedToDate))
          return false;
        if (filters.client && item.clientDisplayName !== filters.client)
          return false;
        if (filters.dueStatus && item.dueStatus !== filters.dueStatus)
          return false;
        return true;
      });

      setTableData(filteredData);
    } catch (err) {
      console.log("List API error:", err);
    }
  };

  const handleClear = () => {
    const resetFilters = {
      type: "All",
      client: "",
      project: "",
      resource: "",
      fromDate: "",
      toDate: "",
      dueDate: "",
      dueStatus: "",
      status: "All",
      changedFromDate: "",
      changedToDate: "",
      submittedBy: "",
    };

    setFilters(resetFilters);

    // ✅ Reload table with NO filters
    handleApply(resetFilters);
  };

  useEffect(() => {
    if (user?.organisationId) {
      handleApply();
    }
  }, [user]);

  //   useEffect(() => {
  //     if (user?.organisationId) {
  //       handleApply();
  //     }
  //   }, [filters]);

  useEffect(() => {
    if (user.organisationId) {
      setFilters((prev) => ({
        ...prev,
        organisationId: user.organisationId,
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await axios.get(`${Base_URL}/ProjectProgress/dropdowns`, {
          params: { orgId: user?.organisationId },
        });

        console.log("Dropdown API:", res.data);

        const data = res?.data?.data || res?.data?.Data || res?.data || {};

        setDropdownData({
          clients: data.clients ?? [],
          projects: data.projects ?? [],
          types: data.types ?? [],
          resources: data.resources ?? [],
          submittedBy: data.submittedBy ?? [],
          dueStatuses: data.dueStatuses ?? [],
          statuses: data.statuses ?? [],
        });
      } catch (err) {
        console.error("Dropdown API error:", err);
      }
    };

    if (user?.organisationId) {
      fetchDropdowns();
    }
  }, [user]);

  return (
    <div className="ppl-container">
      <h2 className="ppl-title">Project Progress List</h2>

      {/* ================= FILTERS ================= */}
      <div className="ppd-filter-box">
        {/* Org ID */}
        <div className="ppd-field">
          <label>Organisation ID</label>
          <input value={user.organisationId || ""} disabled />
        </div>

        {/* Type */}
        <div className="ppd-field">
          <label>Type</label>
          <select
            value={filters.type}
            onChange={(e) => handleChange("type", e.target.value)}
            key={dropdownData.types.join(",")} // 👈 forces refresh
          >
            <option value="All">All</option>

            {(dropdownData.types || []).map((t, i) => (
              <option key={i} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {/* Client */}
        <div className="ppd-field">
          <label>Client</label>
          <select
            value={filters.client}
            onChange={(e) => handleChange("client", e.target.value)}
          >
            <option value="">Select Client</option>

            {(dropdownData.clients || []).map((c, i) => (
              <option key={i} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Project */}
        <div className="ppd-field">
          <label>Project</label>
          <select
            value={filters.project}
            onChange={(e) => handleChange("project", e.target.value)}
          >
            <option value="">Select Project</option>

            {(dropdownData.projects || []).map((p, i) => (
              <option key={i} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* Resource */}
        <div className="ppd-field">
          <label>Assigned To</label>
          <select
            value={filters.resource}
            onChange={(e) => handleChange("resource", e.target.value)}
          >
            <option value="">Select Resource</option>

            {(dropdownData.resources || []).map((r, i) => (
              <option key={i} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div className="ppd-field">
          <label>Due Date</label>
          <input
            type="date"
            value={filters.dueDate}
            onChange={(e) => handleChange("dueDate", e.target.value)}
          />
        </div>

        <div className="ppd-field">
          <label>Submitted By</label>
          <select
            value={filters.submittedBy}
            onChange={(e) => handleChange("submittedBy", e.target.value)}
          >
            <option value="">Select Submitted By</option>

            {(dropdownData.submittedBy || []).map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <br />

        <div className="ppd-field">
          <label>Due Status</label>
          <select
            value={filters.dueStatus}
            onChange={(e) => handleChange("dueStatus", e.target.value)}
          >
            <option value="">All</option>

            {(dropdownData.dueStatuses || []).map((ds, i) => (
              <option key={i} value={ds}>
                {ds}
              </option>
            ))}
          </select>
        </div>

        <div className="ppd-field">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => handleChange("status", e.target.value)}
          >
            <option value="All">All</option>

            {(dropdownData.statuses || []).map((s, i) => (
              <option key={i} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="ppd-date-row">
          <div className="ppd-field">
            <label>Created From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleChange("fromDate", e.target.value)}
            />
          </div>

          <div className="ppd-field">
            <label>Created To Date</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleChange("toDate", e.target.value)}
            />
          </div>
        </div>

        {/* ✅ Changed Date Row */}
        <div className="ppd-date-row">
          <div className="ppd-field">
            <label>Changed From Date</label>
            <input
              type="date"
              value={filters.changedFromDate}
              onChange={(e) => handleChange("changedFromDate", e.target.value)}
            />
          </div>

          <div className="ppd-field">
            <label>Changed To Date</label>
            <input
              type="date"
              value={filters.changedToDate}
              onChange={(e) => handleChange("changedToDate", e.target.value)}
            />
          </div>
        </div>

        <div className="ppd-buttons">
          <button className="apply-btn" onClick={handleApply}>
            Apply
          </button>
          <button className="clear-btn" onClick={handleClear}>
            Clear
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="ppl-table-container">
        <table className="ppl-table">
          <thead>
            <tr>
              {/* <th>Org ID</th> */}
              <th>Org Name</th>
              {/* <th>Client ID</th> */}
              <th>Client Name</th>
              {/* <th>Project ID</th> */}
              <th>Project Name</th>
              <th>Resource</th>
              <th>Due Date</th>
              <th>Due Status</th>
              <th>Created On</th>
              <th>Charged On</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Ticket/Task ID</th>
              <th>Type</th>
              <th>Submitted By</th>
              {/* <th>Task ID</th> */}
            </tr>
          </thead>

          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, index) => (
                <tr key={index}>
                  {/* <td>{row.orgId}</td> */}
                  <td>{row.orgDisplayName}</td>
                  <td>{row.clientDisplayName}</td>
                  {/* <td>{row.projectId}</td> */}
                  <td>
                    {row.projectId ? (
                      <a
                        href={`/project/${row.projectId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Click to view project details"
                        style={{
                          color: "#03275c",
                          textDecoration: "none",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.textDecoration = "none")
                        }
                      >
                        {row.projectName || row.projectId}
                      </a>
                    ) : (
                      row.projectName || "N/A"
                    )}
                  </td>
                  <td>{row.resourceId}</td>
                  <td>
                    {row.dueDate
                      ? new Date(row.dueDate).toLocaleDateString()
                      : ""}
                  </td>
                  <td>{row.dueStatus}</td>

                  <td>
                    {row.createdOn
                      ? new Date(row.createdOn).toLocaleDateString()
                      : ""}
                  </td>
                  <td>
                    {row.chargedOn
                      ? new Date(row.chargedOn).toLocaleDateString()
                      : ""}
                  </td>
                  <td>{row.status}</td>
                  <td>{row.priority}</td>
                  <td>
                    {row.ticketId ? (
                      row.type?.toLowerCase() === "task" ? (
                        <a
                          href={`/project/${row.projectId}/task/${row.ticketId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Click to task details"
                          style={{
                            color: "#03275c",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.textDecoration = "underline")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.textDecoration = "none")
                          }
                        >
                          {row.ticketId}
                        </a>
                      ) : (
                        <Link
                          to={`/ticketinfo/${row.ticketId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Click to view ticket details"
                          style={{
                            color: "#03275c",
                            textDecoration: "none",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.textDecoration = "underline")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.textDecoration = "none")
                          }
                        >
                          {row.ticketId}
                        </Link>
                      )
                    ) : (
                      "N/A"
                    )}
                  </td>
                  {/* <td>{row.taskId}</td> */}
                  <td>{row.type}</td>
                  <td>{row.submittedBy}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="16" className="no-data">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectProgressList;
